import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileDown, FileLock } from "lucide-react";
import { useAppDispatch } from "../../../app/hooks";
import { fetchHeldTrainingRecords } from "../slice-extensions/training-held-records-slice-extension";
import { downloadHeldTrainingRecord } from "../slice-extensions/training-record-slice-extension";
import { HeldTrainingRecord } from "../models/HeldTrainingRecord";
import { formatDateOnly } from "../utils/formatDateOnly";

const PAGE_SIZE = 20;

/**
 * Organization-settings card for the Owner and Admins: the training records kept under legal
 * hold after their learners were erased (§ 31 FahrlG, PD-0021), each downloadable as the
 * Ausbildungsnachweis. Hidden while there are none.
 */
const KeptTrainingRecordsCard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [records, setRecords] = useState<HeldTrainingRecord[]>([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (pageToLoad: number) => {
      const result = await dispatch(fetchHeldTrainingRecords({ page: pageToLoad, pageSize: PAGE_SIZE }));
      if (!fetchHeldTrainingRecords.fulfilled.match(result)) return;
      setRecords((current) => (pageToLoad === 1 ? result.payload.records : [...current, ...result.payload.records]));
      setHasNextPage(result.payload.hasNextPage);
      setPage(pageToLoad);
    },
    [dispatch]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  const download = async (enrolmentId: string) => {
    setBusyId(enrolmentId);
    setError(null);
    const result = await dispatch(downloadHeldTrainingRecord(enrolmentId));
    setBusyId(null);
    if (downloadHeldTrainingRecord.fulfilled.match(result)) return;
    setError(
      result.payload?.statusCode === 409
        ? t("training.record.needsBusinessProfile")
        : result.payload?.message ?? t("training.record.failed")
    );
  };

  if (records.length === 0) return null;

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="mb-1 flex items-center gap-2">
        <FileLock size={20} className="text-primary-40 dark:text-primary-90" />
        <h3 className="text-grey-10 dark:text-grey-90">{t("training.heldRecords.title")}</h3>
      </div>
      <p className="mb-4 text-sm text-grey-50 dark:text-grey-70">{t("training.heldRecords.description")}</p>
      <ul className="divide-y divide-grey-90 border-t border-grey-90 dark:divide-primary-20 dark:border-primary-20">
        {records.map((record) => (
          <li key={record.enrolmentId} className="flex items-center justify-between gap-3 py-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-grey-20 dark:text-white">{record.learnerName}</p>
              <p className="truncate text-xs text-grey-40 dark:text-grey-80">
                {record.programmeName} ·{" "}
                {t("training.heldRecords.keptUntil", { date: formatDateOnly(record.heldUntil, i18n.language) })}
              </p>
            </div>
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1 text-sm text-primary-40 dark:text-primary-90 hover:underline"
              disabled={busyId !== null}
              onClick={() => download(record.enrolmentId)}
            >
              <FileDown size={16} />
              <span>{t("training.record.download")}</span>
            </button>
          </li>
        ))}
      </ul>
      {hasNextPage && (
        <button type="button" className="btn btn-secondary mt-3" onClick={() => load(page + 1)}>
          {t("training.heldRecords.showMore")}
        </button>
      )}
      {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
};

export default KeptTrainingRecordsCard;
